/**
 * SuperAdminEcosystemTab.jsx — the Ecosystem Studio.
 *
 * One place for Ryan to run the information expansion system:
 *  - Blogs: write/edit SEO platform posts (live at /blog/{slug} + sitemap)
 *  - Hopper: the age-based weekly drip every operator/team member receives
 *  - Master switch (OFF = only send-tests go out) + monitor
 */
import { useState, useEffect, useCallback } from 'react';
import { apiErrorText } from '../lib/apiError';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Newspaper, Mail, Activity, Plus, Pencil, Trash2, Send, ExternalLink,
  Power, Loader2, ChevronUp, ChevronDown, X, Megaphone,
} from 'lucide-react';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api/v2/super-admin/ecosystem`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

const CATEGORIES = ['feature', 'integration', 'comparison', 'story', 'how-to'];

const emptyBlog = {
  slug: '', title: '', category: 'feature', excerpt: '', content_md: '',
  hero_image_url: '', seo_title: '', seo_description: '', seo_keywords: '', status: 'published',
};
const emptyEmail = {
  position: 1, subject: '', preheader: '', body_html: '', hero_image_url: '',
  cta_label: '', cta_url: '', blog_slug: '', active: true,
};

const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400';

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-bold text-gray-600 mb-1">{label}{hint && <span className="font-normal text-gray-400 ml-1.5">{hint}</span>}</label>
    {children}
  </div>
);

export default function SuperAdminEcosystemTab() {
  const [tab, setTab] = useState('hopper');
  const [overview, setOverview] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBlog, setEditingBlog] = useState(null);   // object | null
  const [editingEmail, setEditingEmail] = useState(null); // object | null
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [o, b, e] = await Promise.all([
        axios.get(`${API}/overview`, auth()),
        axios.get(`${API}/blogs`, auth()),
        axios.get(`${API}/emails`, auth()),
      ]);
      setOverview(o.data);
      setBlogs(b.data.blogs || []);
      setEmails(e.data.emails || []);
    } catch (err) {
      toast.error('Failed to load Ecosystem data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleMaster = async () => {
    const next = !overview?.enabled;
    if (next && !window.confirm('Turn the weekly drip ON for the whole ecosystem? Sends go out every Monday.')) return;
    try {
      await axios.post(`${API}/toggle`, { enabled: next }, auth());
      toast.success(next ? 'Drip is LIVE — sends every Monday' : 'Drip paused — only send-tests go out');
      load();
    } catch { toast.error('Toggle failed'); }
  };

  const saveBlog = async () => {
    if (!editingBlog.slug || !editingBlog.title || !editingBlog.content_md) { toast.error('Slug, title and content are required'); return; }
    setBusy(true);
    try {
      if (editingBlog.id) await axios.put(`${API}/blogs/${editingBlog.id}`, editingBlog, auth());
      else await axios.post(`${API}/blogs`, editingBlog, auth());
      toast.success('Blog saved — live at /blog/' + editingBlog.slug);
      setEditingBlog(null);
      load();
    } catch (err) {
      toast.error(apiErrorText(err, 'Save failed'));
    } finally { setBusy(false); }
  };

  const deleteBlog = async (b) => {
    if (!window.confirm(`Delete "${b.title}"? The URL /blog/${b.slug} will 404.`)) return;
    try { await axios.delete(`${API}/blogs/${b.id}`, auth()); toast.success('Deleted'); load(); } catch { toast.error('Delete failed'); }
  };

  const saveEmail = async () => {
    if (!editingEmail.subject || !editingEmail.body_html) { toast.error('Subject and body are required'); return; }
    setBusy(true);
    try {
      if (editingEmail.id) await axios.put(`${API}/emails/${editingEmail.id}`, editingEmail, auth());
      else await axios.post(`${API}/emails`, editingEmail, auth());
      toast.success('Email saved to the hopper');
      setEditingEmail(null);
      load();
    } catch (err) {
      toast.error(apiErrorText(err, 'Save failed'));
    } finally { setBusy(false); }
  };

  const deleteEmail = async (e) => {
    if (!window.confirm(`Remove #${e.position} "${e.subject}" from the hopper?`)) return;
    try { await axios.delete(`${API}/emails/${e.id}`, auth()); toast.success('Removed'); load(); } catch { toast.error('Delete failed'); }
  };

  const sendTest = async (e) => {
    try {
      const res = await axios.post(`${API}/emails/${e.id}/send-test`, {}, auth());
      toast.success(`Test sent to ${res.data.sent_to}`);
    } catch { toast.error('Test send failed'); }
  };

  const broadcast = async (e) => {
    if (!window.confirm(
      `📣 BROADCAST "${e.subject}" to the ENTIRE ecosystem right now?\n\n` +
      `Every owner + team member gets it immediately (unsubscribes are skipped, drip positions untouched). There is no undo.`
    )) return;
    try {
      const res = await axios.post(`${API}/emails/${e.id}/broadcast`, {}, auth());
      toast.success(`Broadcast started — sending to ${res.data.recipients} recipients. Watch the Monitor tab.`);
    } catch { toast.error('Broadcast failed to start'); }
  };

  const move = async (idx, dir) => {
    const next = [...emails];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setEmails(next.map((e, i) => ({ ...e, position: i + 1 })));
    try { await axios.post(`${API}/emails/reorder`, { ids: next.map((e) => e.id) }, auth()); } catch { toast.error('Reorder failed'); load(); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div data-testid="ecosystem-studio">
      {/* Header + master switch */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ecosystem Studio</h1>
          <p className="text-sm text-gray-500 mt-1">Blogs feed Google · the hopper feeds the ecosystem, one email a month, age-based from signup</p>
        </div>
        <button
          onClick={toggleMaster}
          data-testid="ecosystem-master-switch"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-colors ${overview?.enabled ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          <Power className="w-4 h-4" />
          {overview?.enabled ? 'Drip is LIVE' : 'Drip is OFF (test mode)'}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        {[
          ['Eligible recipients', overview?.eligible_recipients],
          ['Engaged (started drip)', overview?.engaged_recipients],
          ['Total sends', overview?.total_sends],
          ['Unsubscribed', overview?.unsubscribed],
          ['Blogs live', overview?.blog_count],
        ].map(([label, val]) => (
          <div key={label} className="bg-white border border-gray-100 rounded-xl p-4">
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">{label}</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{val ?? '—'}</p>
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-2 mb-5">
        {[
          ['hopper', 'Email Hopper', Mail],
          ['blogs', 'Blogs', Newspaper],
          ['monitor', 'Monitor', Activity],
        ].map(([id, label, Icon]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            data-testid={`ecosystem-subtab-${id}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === id ? 'bg-[#010d1c] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* ── HOPPER ── */}
      {tab === 'hopper' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">Every new operator/team member starts at #1 when they join — one email a week, no one ever misses one.</p>
            <button
              onClick={() => setEditingEmail({ ...emptyEmail, position: emails.length + 1 })}
              data-testid="ecosystem-add-email"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" /> Add email
            </button>
          </div>
          <div className="space-y-2">
            {emails.map((e, idx) => (
              <div key={e.id} className={`flex items-center gap-3 bg-white border rounded-xl px-4 py-3 ${e.active ? 'border-gray-100' : 'border-dashed border-gray-200 opacity-60'}`} data-testid={`ecosystem-email-row-${e.position}`}>
                <span className="w-8 h-8 rounded-full bg-[#010d1c] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{e.position}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{e.subject}</p>
                  <p className="text-xs text-gray-400 truncate">{e.preheader || '—'}{e.blog_slug ? ` · links /blog/${e.blog_slug}` : ''}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => move(idx, -1)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" title="Move up"><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => move(idx, 1)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" title="Move down"><ChevronDown className="w-4 h-4" /></button>
                  <button onClick={() => sendTest(e)} data-testid={`ecosystem-send-test-${e.position}`} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600" title="Send test to me"><Send className="w-4 h-4" /></button>
                  <button onClick={() => broadcast(e)} data-testid={`ecosystem-broadcast-${e.position}`} className="p-1.5 rounded-lg hover:bg-orange-50 text-orange-500" title="Broadcast to the ENTIRE ecosystem now"><Megaphone className="w-4 h-4" /></button>
                  <button onClick={() => setEditingEmail(e)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" title="Edit"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => deleteEmail(e)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {emails.length === 0 && <p className="text-sm text-gray-400 py-8 text-center">The hopper is empty — add your first email.</p>}
          </div>
        </div>
      )}

      {/* ── BLOGS ── */}
      {tab === 'blogs' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">Published posts go live at /blog/&#123;slug&#125;, join the sitemap automatically, and carry full SEO meta.</p>
            <button
              onClick={() => setEditingBlog({ ...emptyBlog })}
              data-testid="ecosystem-add-blog"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" /> Write a blog
            </button>
          </div>
          <div className="space-y-2">
            {blogs.map((b) => (
              <div key={b.id} className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl px-4 py-3" data-testid={`ecosystem-blog-row-${b.slug}`}>
                <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide flex-shrink-0 ${b.status === 'published' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{b.status}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{b.title}</p>
                  <p className="text-xs text-gray-400 truncate">/blog/{b.slug} · {b.category}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <a href={`/blog/${b.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400" title="View live"><ExternalLink className="w-4 h-4" /></a>
                  <button onClick={() => setEditingBlog(b)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" title="Edit"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => deleteBlog(b)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {blogs.length === 0 && <p className="text-sm text-gray-400 py-8 text-center">No blogs yet.</p>}
          </div>
        </div>
      )}

      {/* ── MONITOR ── */}
      {tab === 'monitor' && (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="px-4 py-3">When</th><th className="px-4 py-3">Recipient</th><th className="px-4 py-3">#</th><th className="px-4 py-3">Subject</th><th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {(overview?.recent_log || []).map((l, i) => (
                <tr key={i} className="border-b border-gray-50">
                  <td className="px-4 py-2.5 text-gray-400 text-xs whitespace-nowrap">{l.ts ? new Date(l.ts).toLocaleString() : '—'}</td>
                  <td className="px-4 py-2.5 text-gray-700">{l.email}</td>
                  <td className="px-4 py-2.5 text-gray-500">{l.position}</td>
                  <td className="px-4 py-2.5 text-gray-700 max-w-[280px] truncate">{l.subject}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${l.status === 'failed' ? 'bg-red-50 text-red-600' : l.status === 'test' ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-700'}`}>{l.status}</span>
                  </td>
                </tr>
              ))}
              {(overview?.recent_log || []).length === 0 && (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-400">Nothing sent yet — try a send-test from the hopper.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── BLOG EDITOR MODAL ── */}
      {editingBlog && (
        <div className="fixed inset-0 z-[90] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={(ev) => { if (ev.target === ev.currentTarget) setEditingBlog(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" data-testid="ecosystem-blog-editor">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h2 className="font-bold text-gray-900">{editingBlog.id ? 'Edit blog' : 'Write a blog'}</h2>
              <button onClick={() => setEditingBlog(null)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Title"><input className={inputCls} value={editingBlog.title} onChange={(e) => setEditingBlog({ ...editingBlog, title: e.target.value })} data-testid="blog-editor-title" /></Field>
                <Field label="Slug" hint="→ bookingflows.com/blog/{slug}"><input className={inputCls} value={editingBlog.slug} onChange={(e) => setEditingBlog({ ...editingBlog, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} data-testid="blog-editor-slug" /></Field>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Category">
                  <select className={inputCls} value={editingBlog.category} onChange={(e) => setEditingBlog({ ...editingBlog, category: e.target.value })}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Status">
                  <select className={inputCls} value={editingBlog.status} onChange={(e) => setEditingBlog({ ...editingBlog, status: e.target.value })}>
                    <option value="published">published</option>
                    <option value="draft">draft</option>
                  </select>
                </Field>
                <Field label="Hero image URL" hint="optional"><input className={inputCls} value={editingBlog.hero_image_url || ''} onChange={(e) => setEditingBlog({ ...editingBlog, hero_image_url: e.target.value })} /></Field>
              </div>
              <Field label="Excerpt" hint="1-2 sentences, shows on listings"><textarea className={`${inputCls} h-16`} value={editingBlog.excerpt || ''} onChange={(e) => setEditingBlog({ ...editingBlog, excerpt: e.target.value })} /></Field>
              <Field label="Content (Markdown)" hint="## headings, **bold**, - lists"><textarea className={`${inputCls} h-64 font-mono text-xs`} value={editingBlog.content_md} onChange={(e) => setEditingBlog({ ...editingBlog, content_md: e.target.value })} data-testid="blog-editor-content" /></Field>
              <div className="rounded-xl bg-blue-50/60 p-4 space-y-3">
                <p className="text-xs font-bold text-blue-900 uppercase tracking-wide">SEO (what Google sees)</p>
                <Field label="SEO title"><input className={inputCls} value={editingBlog.seo_title || ''} onChange={(e) => setEditingBlog({ ...editingBlog, seo_title: e.target.value })} /></Field>
                <Field label="SEO description" hint="~155 chars"><textarea className={`${inputCls} h-16`} value={editingBlog.seo_description || ''} onChange={(e) => setEditingBlog({ ...editingBlog, seo_description: e.target.value })} /></Field>
                <Field label="Keywords" hint="comma-separated"><input className={inputCls} value={editingBlog.seo_keywords || ''} onChange={(e) => setEditingBlog({ ...editingBlog, seo_keywords: e.target.value })} /></Field>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
              <button onClick={() => setEditingBlog(null)} className="px-4 py-2 rounded-full text-sm font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
              <button onClick={saveBlog} disabled={busy} data-testid="blog-editor-save" className="px-5 py-2 rounded-full bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50">{busy ? 'Saving…' : 'Save & publish'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── EMAIL EDITOR MODAL ── */}
      {editingEmail && (
        <div className="fixed inset-0 z-[90] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={(ev) => { if (ev.target === ev.currentTarget) setEditingEmail(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" data-testid="ecosystem-email-editor">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h2 className="font-bold text-gray-900">{editingEmail.id ? `Edit email #${editingEmail.position}` : 'Add email to the hopper'}</h2>
              <button onClick={() => setEditingEmail(null)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="grid sm:grid-cols-4 gap-4">
                <Field label="Position"><input type="number" min="1" className={inputCls} value={editingEmail.position} onChange={(e) => setEditingEmail({ ...editingEmail, position: parseInt(e.target.value || '1', 10) })} /></Field>
                <div className="sm:col-span-3"><Field label="Subject"><input className={inputCls} value={editingEmail.subject} onChange={(e) => setEditingEmail({ ...editingEmail, subject: e.target.value })} data-testid="email-editor-subject" /></Field></div>
              </div>
              <Field label="Preheader" hint="inbox preview line"><input className={inputCls} value={editingEmail.preheader || ''} onChange={(e) => setEditingEmail({ ...editingEmail, preheader: e.target.value })} /></Field>
              <Field label="Body (HTML paragraphs)" hint='use <p style="margin:0 0 14px;">…</p>'><textarea className={`${inputCls} h-48 font-mono text-xs`} value={editingEmail.body_html} onChange={(e) => setEditingEmail({ ...editingEmail, body_html: e.target.value })} data-testid="email-editor-body" /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="CTA label" hint='e.g. "Learn more"'><input className={inputCls} value={editingEmail.cta_label || ''} onChange={(e) => setEditingEmail({ ...editingEmail, cta_label: e.target.value })} /></Field>
                <Field label="CTA URL"><input className={inputCls} value={editingEmail.cta_url || ''} onChange={(e) => setEditingEmail({ ...editingEmail, cta_url: e.target.value })} /></Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Linked blog slug" hint='adds "Read the full write-up →"'><input className={inputCls} value={editingEmail.blog_slug || ''} onChange={(e) => setEditingEmail({ ...editingEmail, blog_slug: e.target.value })} /></Field>
                <Field label="Hero image URL" hint="optional, tops the email"><input className={inputCls} value={editingEmail.hero_image_url || ''} onChange={(e) => setEditingEmail({ ...editingEmail, hero_image_url: e.target.value })} /></Field>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={editingEmail.active} onChange={(e) => setEditingEmail({ ...editingEmail, active: e.target.checked })} className="w-4 h-4 rounded border-gray-300" />
                Active (inactive emails are skipped by the drip)
              </label>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
              <button onClick={() => setEditingEmail(null)} className="px-4 py-2 rounded-full text-sm font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
              <button onClick={saveEmail} disabled={busy} data-testid="email-editor-save" className="px-5 py-2 rounded-full bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50">{busy ? 'Saving…' : 'Save email'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
